import { ReactNode, useEffect } from "react";
import { initials } from "../lib/format";
import { Icon } from "./Icons";
import { useMagnetic } from "../lib/anim";

/* ---- Avatar --------------------------------------------------------- */
export function Avatar({ name, hue = 20, size = 34 }: { name: string; hue?: number; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: size, flexShrink: 0,
      display: "grid", placeItems: "center",
      background: `linear-gradient(140deg, hsl(${hue} 42% 62%), hsl(${hue + 30} 40% 46%))`,
      color: "#fff", fontSize: size * 0.36, fontWeight: 600, letterSpacing: ".02em",
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,.25)",
    }}>{initials(name)}</div>
  );
}

/* ---- Design plate (generative artwork stand-in) --------------------- */
export function Plate({ hue, h = 150, label, seed = 1 }: { hue: number; h?: number; label?: string; seed?: number }) {
  const g = `grad${hue}-${seed}`;
  return (
    <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", height: h }}>
      <svg width="100%" height="100%" viewBox="0 0 400 260" preserveAspectRatio="xMidYMid slice" style={{ display: "block" }}>
        <defs>
          <linearGradient id={g} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={`hsl(${hue} 34% 82%)`} />
            <stop offset="1" stopColor={`hsl(${hue + 24} 30% 60%)`} />
          </linearGradient>
        </defs>
        <rect width="400" height="260" fill={`url(#${g})`} />
        <g stroke="rgba(255,255,255,.45)" strokeWidth="1" fill="none">
          <path d={`M0 ${120 + seed * 8} H400`} />
          <path d={`M${140 + seed * 20} 0 V260`} />
          <rect x={60 + seed * 10} y={70} width="120" height="120" rx="4" />
        </g>
        <g fill="rgba(30,20,12,.10)">
          <circle cx={300} cy={80} r={38} />
        </g>
      </svg>
      {label && <span style={{ position: "absolute", left: 12, bottom: 10, color: "#fff", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".08em", textShadow: "0 1px 4px rgba(0,0,0,.3)" }}>{label}</span>}
    </div>
  );
}

/* ---- Meter ---------------------------------------------------------- */
export function Meter({ value, thin, color }: { value: number; thin?: boolean; color?: string }) {
  return <div className={"meter" + (thin ? " thin" : "")}><i style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color }} /></div>;
}

/* ---- Stat card ------------------------------------------------------ */
export function Stat({ label, children, sub, accent }: { label: string; children: ReactNode; sub?: ReactNode; accent?: boolean }) {
  return (
    <div className="card pad reveal hoverable">
      <div className="kicker">{label}</div>
      <div className="serif" style={{ fontSize: 34, marginTop: 8, color: accent ? "var(--accent-ink)" : "var(--ink)" }}>{children}</div>
      {sub && <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

/* ---- Page header ---------------------------------------------------- */
export function PageHead({ kicker, title, children }: { kicker: string; title: string; children?: ReactNode }) {
  return (
    <div className="row between wrap reveal" style={{ marginBottom: 26, gap: 16 }}>
      <div>
        <div className="kicker">{kicker}</div>
        <h1 className="serif" style={{ fontSize: 38, marginTop: 6 }}>{title}</h1>
      </div>
      {children && <div className="row wrap" style={{ gap: 10 }}>{children}</div>}
    </div>
  );
}

/* ---- Button with optional magnetic hover ---------------------------- */
export function MagButton({ children, className = "btn", onClick, disabled }: { children: ReactNode; className?: string; onClick?: () => void; disabled?: boolean }) {
  const ref = useMagnetic<HTMLButtonElement>(0.25);
  return <button ref={ref} className={className} onClick={onClick} disabled={disabled}>{children}</button>;
}

/* ---- Modal ---------------------------------------------------------- */
export function Modal({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title?: string; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    if (!open) return;
    const f = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", f);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", f); document.body.style.overflow = ""; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div onClick={onClose} className="modal-wrap" style={{
      position: "fixed", inset: 0, zIndex: 90, display: "grid", placeItems: "center", padding: 20,
      background: "rgba(20,14,8,.42)", backdropFilter: "blur(6px)", animation: "floatIn .25s ease",
    }}>
      <div onClick={(e) => e.stopPropagation()} className="card modal-card" style={{

        width: "100%", maxWidth: wide ? 720 : 480, padding: 26, boxShadow: "var(--shadow-lg)",
        animation: "floatIn .4s var(--ease-out)", maxHeight: "88vh", overflow: "auto",
      }}>
        {title && (
          <div className="row between" style={{ marginBottom: 18 }}>
            <h3 className="serif" style={{ fontSize: 24 }}>{title}</h3>
            <button className="iconbtn" onClick={onClose} aria-label="Close"><Icon.close /></button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

/* ---- Empty state ---------------------------------------------------- */
export function Empty({ children }: { children: ReactNode }) {
  return <div className="panel" style={{ padding: 40, textAlign: "center", color: "var(--ink-3)", borderStyle: "dashed" }}>{children}</div>;
}

/* ---- Segmented control ---------------------------------------------- */
export function Seg<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: string }[] }) {
  return (
    <div className="row" style={{ gap: 6, background: "var(--paper-2)", padding: 4, borderRadius: 100, border: "1px solid var(--line)" }}>
      {options.map((o) => (
        <button key={o.value} className={"chip" + (value === o.value ? " on" : "")} style={{ border: "none", background: value === o.value ? "var(--ink)" : "transparent" }} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/* ---- Status badge helpers ------------------------------------------- */
export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { cls: string; label: string }> = {
    approved: { cls: "ok", label: "Approved" }, accepted: { cls: "ok", label: "Accepted" }, paid: { cls: "ok", label: "Paid" }, done: { cls: "ok", label: "Complete" },
    sent: { cls: "info", label: "Sent" }, due: { cls: "warn", label: "Due" }, doing: { cls: "info", label: "In progress" }, active: { cls: "accent", label: "Active" },
    changes: { cls: "warn", label: "Changes requested" }, revise: { cls: "warn", label: "Revise" }, scheduled: { cls: "", label: "Scheduled" },
    draft: { cls: "", label: "Draft" }, pending: { cls: "", label: "Awaiting" }, todo: { cls: "", label: "To do" }, upcoming: { cls: "", label: "Upcoming" },
    overdue: { cls: "danger", label: "Overdue" }, declined: { cls: "danger", label: "Declined" }, blocked: { cls: "danger", label: "Blocked" }, issued: { cls: "ok", label: "Issued" },
  };
  const m = map[status] ?? { cls: "", label: status };
  return <span className={"badge " + m.cls}><span className="dot" />{m.label}</span>;
}
